// The footer on this page is hidden when it is everplans branded cobranding style becasue of a check in `Footer.react` --ZD
import {connect} from 'react-redux'
import {List, Map} from 'immutable'
import {useQuery} from '@apollo/client'
import PropTypes from 'prop-types'
import {useState, useCallback, useEffect, Fragment} from 'react'
import {readEndpoint} from 'redux-json-api'


import AuthAppClassSideEffect from '../AuthAppClassSideEffect'
import HeaderFlavor from '../../../../shared_components/header/HeaderFlavor'
import PageTitle from '../../../../shared_components/PageTitle'
import TfaSetup from './TfaSetup'
import Loader from '../../../../shared_components/NewLoader'

import SessionStore from '../../../../auth/src/stores/SessionStore'
import TfaConfigStore from '../../stores/TfaConfigStore'
import TfaWizardStore from '../../stores/TfaWizardStore'
import {cleanGraphQLResponse} from '../../../../lib/graphQLTools'
import {findRawResourceByAttributeId, findRawResourceById} from '../../../../lib/plan_data/userData'
import {currentUserConfig, userPartner} from '../../../../lib/userTools'
import {needsFirmOrganizationConfig} from '../../../../lib/tools'
import {onTfaExit} from '../../../../lib/interstitialTools'
import {isFirmUserWhitelistedForTfaSkip} from '../../../../lib/tfaTools'
import {TFA_ENROLLMENTS} from '../../../../graphql/queries/tfa'

import './tfaIntro.scss'

const TfaIntro = props => {
  const [currentUser] = useState(SessionStore.getState().currentUser)
  const [tfaConfig, setTfaConfig] = useState(TfaConfigStore.getTfaConfig())
  const [wizardActive, setWizardActive] = useState(TfaWizardStore.active())
  const {data, loading} = useQuery(TFA_ENROLLMENTS)
  const appEnrollmentOnly = props.userConfig.get('admin') || props.userConfig.get('quarterback') || props.userConfig.get('account-manager')
  const tfaEnrollments = cleanGraphQLResponse(data, 'tfaEnrollments', List())

  const onUpdate = () => {
    setTfaConfig(TfaConfigStore.getTfaConfig())
    setWizardActive(TfaWizardStore.active())
  }

  const exitTfa = useCallback(event => {
    if (event)
      event.preventDefault()

    onTfaExit(props, currentUser)
  }, [props.location.query.loc, currentUser, props.interstitial, props.partner])

  useEffect(() => {
    props.readEndpoint('user-configs')
      .then(response => {
        // Using the everplan id from the response in situation where user config does not already exist in the store when this request is initiated

        // only clients belonging to a firm with mandatory TFA won't be a premium user at this point and as such this makes sure that needed firm data is fetched
        if (needsFirmOrganizationConfig(response.body.data.attributes) || currentUser.has_interstitials || currentUser.firm_user) {
          // need organization config when current user is a firm user 'cause they'll only be sent here outside of onboarding if their firm has mandatory TFA
          if (currentUser.firm_user)
            props.readEndpoint(`firms/${currentUser.firm}?include=client-seats,firm-config,disclosure,firm-seats,organization.organization-config`)
          else
            props.readEndpoint(`everplans/${response.body.data.attributes['everplan-id']}?include=firm.firm-config,firm.organization.organization-config,firm-ownership`)
        }
      })

    props.readEndpoint(`users/${currentUser.id}?include=partner`)

    TfaConfigStore.addChangeListener(onUpdate)
    TfaWizardStore.addChangeListener(onUpdate)

    return () => {
      TfaConfigStore.removeChangeListener(onUpdate)
      TfaWizardStore.removeChangeListener(onUpdate)
    }
  }, [])

  useEffect(() => {
    // only app enrollment mandated user type can get here after enabling TFA
    if (
      !appEnrollmentOnly && props.userConfig.get('tfa-enabled') ||
      (appEnrollmentOnly && tfaEnrollments.some(enrollment => enrollment.get('mode') === 'app' && !!enrollment.get('enabled-at')))
    )
      exitTfa()
  }, [props.userConfig, tfaEnrollments.size])

  return (
    <Fragment>
      <HeaderFlavor flavor='transactional' />
      <PageTitle title='Two-Factor Authentication Settings' />
      {!props.interstitial && <AuthAppClassSideEffect className='auth-interstitial' />}
      {
        props.loading || loading ?
          <Loader loading={true} /> :
          <div className='tfa-intro'>
            <TfaSetup
              exitTfa={exitTfa}
              firmUser={currentUser.firm_user}
              isQuarterback={currentUser.quarterback}
              currentUser={currentUser}
              onboarding={!!props.location.query.loc || props.interstitial}
              tfaConfig={tfaConfig}
              wizardActive={wizardActive}
              firmConfig={props.firmConfig}
              organizationConfig={props.organizationConfig}
              isFuneralHome={props.isFuneralHome}
              isWhitelistedFirmUser={props.isWhitelistedFirmUser}
              readEndpoint={props.readEndpoint}
              tfaEnrollments={tfaEnrollments}
              userConfig={props.userConfig}
            />
          </div>
      }
    </Fragment>
  )
}

TfaIntro.contextTypes = {router: PropTypes.object}

TfaIntro.propTypes = {
  firmConfig: PropTypes.instanceOf(Map),
  interstitial: PropTypes.bool,
  loading: PropTypes.bool,
  location: PropTypes.shape({query: PropTypes.object}),
  next: PropTypes.func,
  organizationConfig: PropTypes.instanceOf(Map),
  partner: PropTypes.instanceOf(Map),
  readEndpoint: PropTypes.func,
  isFuneralHome: PropTypes.bool,
  userConfig: PropTypes.instanceOf(Map),
  isWhitelistedFirmUser: PropTypes.bool,
  tfaEnrollments: PropTypes.instanceOf(List)
}

const mapStateToProps = state => {
  const userConfig = currentUserConfig(state.api)

  const firmOwnership = findRawResourceByAttributeId({
    rawResource: state.api['firm-ownerships'],
    attribute: 'everplan-id',
    id: userConfig.get('everplan-id')
  })
  const firm = findRawResourceById({
    rawResource: state.api.firms,
    id: firmOwnership.get('ownable-id') || userConfig.get('firm-id')
  })

  const organizationConfig = findRawResourceByAttributeId({
    rawResource: state.api['organization-configs'],
    attribute: 'organization-id',
    id: firm.get('organization-id')
  })

  const firmConfig = findRawResourceByAttributeId({
    rawResource: state.api['firm-configs'],
    attribute: 'firm-id',
    id: firm.get('id')
  })

  const partner = userPartner({userConfig, partners: state.api.partners})
  const isFuneralHome = firm && (firm.get('firm-type') === 'funeral_home')
  const isWhitelistedFirmUser = isFirmUserWhitelistedForTfaSkip({
    firmUserId: userConfig.get('id'),
    settings: state.api.settings
  })

  return {
    userConfig,
    firmConfig,
    organizationConfig,
    loading: userConfig.isEmpty() || (!!userConfig.get('firm-id') && firm.isEmpty()),
    partner,
    isFuneralHome,
    isWhitelistedFirmUser
  }
}

export default connect(mapStateToProps, {readEndpoint})(TfaIntro)
